import React from 'react'
import styled from 'styled-components'
import { graphql, withApollo, compose } from 'react-apollo'
import PropTypes from 'prop-types'
import moment from 'moment'
import Cleave from 'cleave.js/react'
import { DayPickerSingleDateController } from 'react-dates'
import UpdateFiscalYear from './queries/UpdateFiscalYear'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import CacheUtil from '../common/utils/CacheUtil'
import LoanCashflowQuery from './queries/LoanCashflowQuery'
import BusinessCashflowQuery from './queries/BusinessCashflowQuery'

const PAndLContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`
const FileIcon = styled.a`
  display: inline;
  color: #dd6a01;
`
const DatePickerContainer = styled.div`
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'all' : 'none'};
  position: absolute;
  top: 60px;
  right: 10px;
  z-index: 10000;
  border: 1px solid #EEEEEE;
  grid-column-start: 1;
  grid-column-end: 3;
  background: white;
`
const DateInput = styled(Cleave)`
  width: 40%;
  padding: 4px 4px !important;
  background: white !important;
  cursor: text !important;
  grid-column-start: 1;
  grid-column-end: 4;
  font-size: 80% !important;
  text-align: center;
  padding: 0px !important;
  height: 25px;
  justify-self: end;
  font-size: 10px;
  margin-bottom: 0px !important; /* Bootstrap override */
`
const StartDate = styled.span`
  font-size: 10px;
  margin-right: 5px;
`
const Dates = styled.small`
  grid-column-start: 1;
  grid-column-end: 4;
  font-size: 85%;
  white-space: nowrap;
`

class PLDatePicker extends React.Component {
  constructor(props) {
    super()
    this.state = {
      isDatePickerOpen: false,
      date: props.pAndLEndDate ? props.pAndLEndDate : null
    }
  }
  componentWillReceiveProps(nextProps) {
    const { date } = this.state
    const { pAndLEndDate, isEditable, resourceType } = this.props
    if (date && pAndLEndDate && !pAndLEndDate.isSame(date) && isEditable && !nextProps.isEditable && resourceType === 'Business') {
      this._changeDate()
    }
    if (!nextProps.pAndLEndDate.isSame(pAndLEndDate)) {
      this.setState({ date: nextProps.pAndLEndDate })
    }
  }
  isDayBlocked = day => {
    const { fiscalYearStartDate, year } = this.props
    const diff = day.diff(fiscalYearStartDate, 'd', true)
    if (moment([year], 'YYYY').isLeapYear()) {
      return diff >= 366 || diff < 0
    } else {
      return diff >= 365 || diff < 0
    }
  }
  onDatePick = date => {
    this.dateCleave.setRawValue(date.format('MM/DD'))
    this.setState({
      date,
      isDatePickerOpen: false
    })
  }
  onDateClick = () => {
    this.setState({
      isDatePickerOpen: true
    })
  }
  onDateChange = e => {
    const { fiscalYearStartDate, year } = this.props
    const { value } = e.target
    const endDate = moment(value, 'MM/DD', true)
    if (!endDate.isValid()) {
      return
    }
    const startMonth = fiscalYearStartDate.month()
    const startDay = fiscalYearStartDate.date()
    const endMonth = endDate.month()
    const endDay = endDate.date()
    const endYear = startMonth <= endMonth && startDay <= endDay ? year : year + 1
    endDate.year(endYear)
    this.setState({
      date: endDate
    })
  }
  _changeDate = () => {
    const {
      fiscalYearStartDate,
      updateFiscalYear,
      fiscalYear,
      resourceType,
      cashflowOptions,
      queriedYears,
      loan,
      entity,
      client
    } = this.props
    const { date } = this.state
    const { rid, year } = fiscalYear
    // We add one because moment considers 365 days to be the difference
    // between the same day, one year apart. To display the correct date,
    // we subtracted one. In order to write the correct number of days to
    // the FiscalYear, we must add one.
    const days = date.diff(fiscalYearStartDate, 'd') + 1
    const queryVariables = { loanId: loan.rid, years: queriedYears, cashflowOptions }
    return updateFiscalYear({
      variables: {
        entityType: resourceType,
        rid,
        days
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateFiscalYear: {
          fiscalYear: {
            ...fiscalYear,
            days,
            __typename: 'FiscalYear',
            optimistic: true
          }
        }
      },
      update: async (store, { data: { updateFiscalYear: fiscalYear } }) => {
        const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
        const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
        CacheUtil.replaceFiscalYear(cachedEntity, fiscalYear)
        store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
        if (!fiscalYear.optimistic) {
          await CacheUtil.refetchEntityCashflowData(
            client,
            { entity: loan, years: [year], cashflowOptions },
            queryVariables,
            true
          )
          await CacheUtil.refetchEntityCashflowData(
            client,
            { entity, years: [year], cashflowOptions },
            queryVariables,
            true
          )
        }
      }
    })
  }
  render() {
    const { pLUrl, isEditable, resourceType, fiscalYearStartDate, fiscalYear, entity } = this.props
    const { isDatePickerOpen, date } = this.state
    const { period, year } = fiscalYear
    const isLastInterim = Math.max(...entity.fiscalYears.map(fy => fy.year)) === year
    /* HACK: There is a missing feature in react-dates that prevents the DayPickerSingleDateController
     * from updating when its 'date' prop is changed. To counter this, we forcibly remount the date picker
     * by giving it a new 'key' prop whenever the date is changed. See https://github.com/airbnb/react-dates/issues/616
     */
    if (isEditable && period === 'interim' && resourceType === 'Business' && isLastInterim) {
      return (
        <PAndLContainer>
          <StartDate>{fiscalYearStartDate.format('MM/DD')} - </StartDate>
          <DateInput
            type="text"
            onChange={this.onDateChange}
            value={date.format('MM/DD')}
            id={`${year}_date_input`}
            options={{
              date: true,
              datePattern: ['m', 'd']
            }}
            onClick={this.onDateClick}
            onInit={cleave => this.dateCleave = cleave}
          />
          <DatePickerContainer visible={isDatePickerOpen}>
            <DayPickerSingleDateController
              hideKeyboardShortcutsPanel
              date={date}
              onDateChange={this.onDatePick}
              focused
              onFocusChange={() => {}}
              numberOfMonths={1}
              noBorder
              verticalHeight={300}
              onOutsideClick={() => this.setState({ isDatePickerOpen: false })}
              enableOutsideDays={false}
              transitionDuration={0}
              isDayBlocked={this.isDayBlocked}
              isDayHighlighted={day => day.isSame(date)}
              // ref={node => this.datePicker = node}
              key={`${Math.random() * Math.random()}`}
            />
          </DatePickerContainer>
        </PAndLContainer>
      )
    } else if (period === 'interim' && resourceType === 'Business') {
      return (
        <PAndLContainer>
          <Dates>
            {
              pLUrl &&
              <FileIcon
                href={`${window.location.protocol}//${window.location.host}${pLUrl}`}
                target="_blank"
                rel="noreferrer"
                className="fa fa-file-text-o"
                id={`${year}_p_and_l`}
              />
            }
            &nbsp;
            {
              fiscalYearStartDate && date && resourceType === 'Business' &&
              <small>
                {fiscalYearStartDate.format('MM/DD')} - {date.format('MM/DD')}
              </small>
            }
          </Dates>
        </PAndLContainer>
      )
    }
    return null
  }
}

PLDatePicker.propTypes = {
  fiscalYear: PropTypes.shape({
    year: PropTypes.number.isRequired,
    period: PropTypes.string.isRequired
  }),
  fiscalYearStartDate: PropTypes.object,
  isEditable: PropTypes.bool,
  pAndLEndDate: PropTypes.shape({
    clone: PropTypes.func.isRequired,
    year: PropTypes.func.isRequired
  }),
  period: PropTypes.string,
  pLUrl: PropTypes.string,
  resourceType: PropTypes.string,
  updateFiscalYear: PropTypes.func.isRequired,
  year: PropTypes.number.isRequired
}

export default compose(
  graphql(UpdateFiscalYear, {
    name: 'updateFiscalYear'
  }),
  withApollo
)(PLDatePicker)
